import { Autocomplete, CircularProgress, FormControl, TextField } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

export default function InfiniteScrollListBlog({
  uiScreen,
  dataFetcher = () => { },
  options = [],
  optionSelectHandler = () => { },
  value = "",
  label = "Select",
  multiple = true,
  maxSelection = 3,
}) {
  const dispatch = useDispatch();
  const cachedOptions = useRef({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [lists, setLists] = useState([]);
  const observer = useRef();
  const [searchText, setSearchText] = useState("");

  // Initialize cachedOptions with current value if it's already provided
  useEffect(() => {
    if (multiple && Array.isArray(value)) {
      // We might not have labels for initial values if they come from DB
      // But we can try to find them in lists
      value.forEach(v => {
        const id = v?.blogId || v?.value || v;
        if (!cachedOptions.current[id]) {
          const found = lists.find(opt => opt.value === id);
          if (found) {
            cachedOptions.current[id] = found.label;
          } else if (v?.title) {
            // If the value object already has a title, cache it
            cachedOptions.current[id] = v.title;
          }
        }
      });
    } else if (!multiple && value) {
      const id = value?.blogId || value?.value || value;
      if (!cachedOptions.current[id]) {
        const found = lists.find(opt => opt.value === id);
        if (found) {
          cachedOptions.current[id] = found.label;
        } else if (value?.title) {
          // If the value object already has a title, cache it
          cachedOptions.current[id] = value.title;
        }
      }
    }
  }, [value, lists, multiple]);

  const loadMoreData = useCallback(async (currentSearch = searchText, currentPage = page) => {
    setLoading(true);
    try {
      const limit = 10;
      const skip = currentPage * limit;
      const params = { skip, limit };
      if (currentSearch) {
        params.search = currentSearch;
      }
      const res = await dispatch(dataFetcher(params)).unwrap();

      let newPosts = [];
      if (Array.isArray(res)) {
        newPosts = res;
      } else if (Array.isArray(res?.blogs)) {
        newPosts = res.blogs;
      } else if (Array.isArray(res?.data)) {
        newPosts = res.data;
      } else if (res && typeof res === 'object') {
        newPosts = Object.values(res).filter(x => x && typeof x === 'object' && ('_id' in x || '_id' in x || 'id' in x));
      }

      const formattedPosts = newPosts.map(post => ({
        value: post?._id || post?.id || post?._id,
        label: post?.title
      }));

      // Cache all newly loaded options
      formattedPosts.forEach(post => {
        cachedOptions.current[post.value] = post.label;
      });

      if (newPosts?.length === 0) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
      return formattedPosts;
    } catch (err) {
      console.error(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [dispatch, dataFetcher, page, searchText]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(0);
      setHasMore(true);
      loadMoreData(searchText, 0).then(formattedPosts => {
        setLists(formattedPosts);
      });
    }, 500);
    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    if (hasMore && page > 0) {
      loadMoreData(searchText, page).then(formattedPosts => {
        setLists(prev => {
          const next = [...prev];
          formattedPosts.forEach(post => {
            if (!next.some(p => p.value === post.value)) {
              next.push(post);
            }
          });
          return next;
        });
      });
    }
  }, [page]);


  const lastPostElementRef = useCallback(
    (node) => {
      if (loading || !hasMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setPage((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  // Helper to get full option object from value (ID or object)
  const getOptionFromValue = (v) => {
    if (typeof v === 'object' && v !== null) {
      // If it's already an object with blogId/title or value/label structure
      if ('blogId' in v && 'title' in v) {
        return { value: v.blogId, label: v.title };
      }
      if ('value' in v && 'label' in v) {
        return v;
      }
    }
    // If it's just an ID string
    return {
      value: v,
      label: cachedOptions.current[v] || v // Fallback to ID if label not cached
    };
  };

  return (
    <FormControl fullWidth>
      <Autocomplete
        multiple={multiple}
        disableCloseOnSelect={multiple}
        inputValue={searchText}
        options={lists}
        loading={loading}
        value={multiple
          ? (Array.isArray(value) ? value.map(getOptionFromValue) : [])
          : (value ? getOptionFromValue(value) : null)}
        onChange={(event, newValue) => {
          if (multiple) {
            if (newValue.length <= maxSelection) {
              // Return objects with blogId and title structure
              const result = newValue.map(v => ({
                blogId: v.value,
                title: v.label
              }));
              optionSelectHandler(result);
              setSearchText("")
            }
          } else {
            // Return single object with blogId and title structure or empty string
            const result = newValue ? { blogId: newValue.value, title: newValue.label } : '';
            optionSelectHandler(result);
            setSearchText("")
          }
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'input' || reason === 'clear') {
            setSearchText(newInputValue);
          } else if (reason === 'reset') {
            // Keep the search text even after selection to allow "same search" to continue
            // Only clear if the user explicitly clears it (reason === 'clear')
          }
        }}
        onOpen={() => {
          if (lists.length === 0 || (searchText === "" && page === 0)) {
            loadMoreData(searchText, 0).then(formattedPosts => {
              setLists(formattedPosts);
            });
          }
        }}
        getOptionLabel={(option) => option.label || String(option.value) || ''}
        filterOptions={(x) => x}
        isOptionEqualToValue={(option, val) => {
          const optionValue = option.value;
          const compareValue = val?.value || val?.blogId || val;
          return optionValue === compareValue;
        }}
        getOptionDisabled={(option) =>
          multiple && Array.isArray(value) && value.length >= maxSelection &&
          !value.some(v => (v?.value || v?.blogId || v) === option.value)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        ListboxProps={{
          style: {
            maxHeight: 400,
            overflow: 'auto'
          },
          ref: (node) => {
            if (node && node.lastChild && lists?.length > 0) {
              lastPostElementRef(node.lastChild);
            }
          }
        }}
      />
    </FormControl>
  );
}


